import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import "../styles/Schemes.css";
import { ReactComponent as EditPencilIcon } from "../assets/rewamp/onboarding/edit-pencil-icon.svg";
import RewampFieldLabel from "../rewamp/components/schemeApply/formEngine/widgets/RewampFieldLabel";
import { useDispatch } from "react-redux";
import OTPinput from "./OTPinput";
import { SchemeService } from "../services/Schemes";
import { dispatchNotification } from "./Utils";
import { CssTextField } from "./Form/CustomWidget";
import AccessibleCircularProgress from "./AccessibleCircularProgress";
import generateRewampTheme from "../rewamp/rewampTheme";
import { AppLayoutContext } from "../contexts/AppLayoutContext";
import RewampFeedbackBanner from "../rewamp/components/RewampFeedbackBanner";
import { RewampFieldLabel as OnboardingFieldLabel } from "../rewamp/components/RewampFormField";
import {
  DBT_REGISTRATION_PORTAL_URL,
  LOGIN_CARD_MAX_WIDTH,
  onboardingCardSx,
  onboardingInputSx,
  OTP_RESEND_SECONDS,
} from "../rewamp/components/RewampOnboardingLayout";
import {
  isOtpErrorVariant,
  resolveOtpFeedbackVariant,
} from "../rewamp/utils/otpFeedbackVariant";
import { OTP_RATE_LIMIT_CODE } from "../rewamp/utils/otpApiErrors";
import useUrlDialog from "../Hooks/useUrlDialog";
import UrlDialog from "./dialog/UrlDialog";
import useDbtRegistrationHint from "../rewamp/hooks/useDbtRegistrationHint";
import { DbtRegistrationBadge } from "../rewamp/components/DbtRegistrationBadge";
import { isProfileIdentityCategoryType } from "../rewamp/context/FarmerContext";

// Backported from rp-revamp-scheme-3547-handoff ahead of this branch's
// eventual rebase (BLOCKERS.md B13) — needed by FarmerLoginForm.js's OTP
// step (Figma 91-28910). `FarmerDbtVerification` below (its card, radio-tab
// DBT-ID/mobile switch, CssTextField, legacy CustomButton for step 1) is
// UNCHANGED — only this sub-component was pulled forward. BUT since
// `OtpInputRenderer` is the same shared symbol FarmerDbtVerification already
// calls for its own OTP step (`:420` below), plus InlineFarmerOtp.js and
// GRM's TrackService.js/GrmCreateForm.js, this backport also upgrades THEIR
// OTP screens to the new boxed/pencil/rewamp-themed look — not a side effect
// to work around: this is the same "let every OtpInputRenderer consumer
// follow rewampTheme.js" convergence already decided and shipped on
// rp-revamp-scheme-3547-handoff (~/code/notes/rewamp/farmerContext/PLAN.md,
// "OtpInputRenderer blast radius is component-level, not just CSS-level").
// Bringing it here early is adopting that validated decision, not inventing
// a new one — see notes/rewamp/onboarding/PLAN.md.
//
// OtpInputRenderer's own body — always rendered inside the rewamp theme (see
// the exported OtpInputRenderer wrapper below), so `useTheme()` here resolves
// to rewampTheme.js regardless of which page embeds this component.
// Standard visually-hidden pattern — keeps the "Verify using" fieldset legend
// accessible to screen readers even though Figma 639-82351 doesn't show it
// visually (the tab-styled radio pair is self-explanatory sighted).
const visuallyHiddenSx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// Figma 639-88408 — bordered card shell for the OTP step, shared by every
// OtpInputRenderer consumer (GRM's TrackService.js/GrmCreateForm.js included).
const otpCardSx = {
  p: (theme) => theme.palette.layout.cardPadding,
  backgroundColor: "background.paper",
  borderRadius: "16px",
  border: 1,
  borderColor: "divider",
  boxShadow: (theme) => theme.palette.layout.cardShadow,
};

// "Didn't Receive? Retry in 00:00s" — design wants mm:ss, not a raw second count.
const formatCountdown = (seconds) => {
  const total = Math.max(0, seconds);
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

// Splits an already-interpolated "...{{time}}..." sentence around the known
// countdown substring so only the parameterised time value can be colored —
// language-agnostic (no dependency on where {{time}} sits in the sentence),
// unlike wrapping it in Trans-style markup in every locale's translation string.
const renderCountdownSentence = (sentence, countdown, timeSx) => {
  const idx = sentence.indexOf(countdown);
  if (idx === -1) return sentence;
  // A unit glued to the value ("00:30s") belongs to it; a space before the next
  // word (Hindi "{{time}} में") means the sentence continues, so nothing extends.
  const unit = sentence.slice(idx + countdown.length).match(/^\p{L}+/u)?.[0] ?? "";
  const end = idx + countdown.length + unit.length;
  return (
    <>
      {sentence.slice(0, idx)}
      <Box component="span" sx={timeSx}>
        {countdown}
        {unit}
      </Box>
      {sentence.slice(end)}
    </>
  );
};

// Figma 639-70196 / 91-28910 — shared onboarding OTP vertical rhythm (8px grid).
const ONBOARDING_OTP_GAP_TITLE = 2;
const ONBOARDING_OTP_GAP_LABEL = 1.5;
const ONBOARDING_OTP_GAP_INPUTS = 2;

const formatOtpMobileDisplay = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
  if (digits.length === 10) return digits;
  return String(value ?? "");
};

const renderOnboardingEditPencil = (t, onEditNumber) => (
  <IconButton
    size="small"
    aria-label={t("schemes.editMobileNumber")}
    onClick={onEditNumber}
    sx={{
      color: "primary.main",
      p: 0.5,
      minWidth: 24,
      minHeight: 24,
    }}
  >
    <EditPencilIcon width={18} height={18} aria-hidden focusable="false" />
  </IconButton>
);

const OtpInputRendererBody = (props) => {
  const {
    mobileNumber,
    otpValue,
    setOtpValue,
    otpValidationError,
    setOtpValidationError,
    t,
    handleVerifyOtp,
    isSubmit = false,
    onResendOtp,
    onEditNumber,
    bare = false,
    waitTime,
    setWaitTime,
    verificationLoading = false,
    onboardingChrome = false,
    otpSubheaderKey = "",
    hideAccountVerificationTitle = false,
    mobileNumberDisplay = "",
    layout = "stacked",
  } = props;

  const theme = useTheme();
  const otpMobileDisplay = formatOtpMobileDisplay(mobileNumberDisplay || mobileNumber);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (timeLeft > 0) {
      const interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    } else if (timeLeft === 0) {
      setWaitTime(null);
      // Countdown ran out — surface the expiry state on every OtpInputRenderer
      // consumer (not just onboardingChrome), not only when Verify is clicked.
      setOtpValidationError({ message: "ONBOARDING_OTP_EXPIRED" });
    }
  }, [timeLeft, setWaitTime, setOtpValidationError]);

  useEffect(() => {
    setTimeLeft(waitTime);
  }, [waitTime]);

  const handleOTPClick = async () => {
    if (otpValue.length < 6) {
      setOtpValidationError({ message: "COMMON_OTP_REQUIRED" });
      return;
    }
    if (onboardingChrome && timeLeft === 0 && (waitTime === null || waitTime === 0)) {
      setOtpValidationError({ message: "ONBOARDING_OTP_EXPIRED" });
      return;
    }
    handleVerifyOtp();
  };

  // "inline" sits in a full-width page column, so the digit row must stay at
  // content width and left-align — centring it strands the digits mid-page,
  // away from both the label above and the Verify button beside it.
  const isInline = layout === "inline";
  const otpHasError = isOtpErrorVariant(otpValidationError);
  const otpFeedbackVariant = resolveOtpFeedbackVariant(otpValidationError?.message);

  const otpBoxSx = {
    width: "48px",
    height: "52px",
    borderRadius: onboardingChrome ? "10px" : "8px",
    border: `1px solid ${otpHasError ? theme.palette.formMessage.error : theme.palette.primary.interactive}`,
    backgroundColor: otpHasError
      ? theme.palette.feedbackBanner.errorBg
      : theme.palette.primary.softSurface || theme.palette.background.default,
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: "20px",
    textAlign: "center",
  };

  const renderOtpFeedback = () => {
    if (!otpValidationError?.message || !otpFeedbackVariant) return null;
    return (
      <RewampFeedbackBanner
        variant={otpFeedbackVariant}
        message={t(otpValidationError.message)}
        sx={{ maxWidth: onboardingChrome || isInline ? "100%" : "386px" }}
      />
    );
  };

  const renderResendRow = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: isInline ? "flex-start" : "flex-end",
        width: isInline ? "auto" : "100%",
        pb: onboardingChrome ? 2 : 0,
        pt: onboardingChrome ? 0 : 0,
      }}
    >
      {timeLeft > 0 ? (
        onboardingChrome ? (
          <Typography sx={{ fontSize: 12, lineHeight: "18px", color: "neutral.disabled", textAlign: "right" }}>
            {renderCountdownSentence(
              t("ONBOARDING_OTP_RETRY_IN", { time: formatCountdown(timeLeft) }),
              formatCountdown(timeLeft),
              { color: "primary.main", fontWeight: 600 }
            )}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: "text.primary" }}>
            {renderCountdownSentence(
              t("schemes.resendOtpIn", { time: formatCountdown(timeLeft) }),
              formatCountdown(timeLeft),
              { color: "primary.main", fontWeight: 600, textDecoration: "underline" }
            )}
          </Typography>
        )
      ) : (
        <Button
          variant="text"
          size="small"
          onClick={() => {
            // Resending clears whatever error (e.g. expired-OTP) was showing
            // for the old code — it no longer applies to the new one.
            setOtpValidationError({ message: "" });
            onResendOtp?.();
          }}
          sx={{
            textDecoration: "underline",
            color: theme.palette.primary.main,
            fontWeight: onboardingChrome ? 600 : 400,
            fontSize: onboardingChrome ? 12 : undefined,
            lineHeight: onboardingChrome ? "18px" : undefined,
            paddingX: "0px !important",
          }}
        >
          {t("COMMON_RESEND_OTP")}
        </Button>
      )}
    </Box>
  );

  const renderOnboardingChrome = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      {!hideAccountVerificationTitle ? (
        <Typography
          variant="h2"
          component="p"
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: "30px",
            textAlign: "center",
            color: "text.primary",
            width: "100%",
          }}
        >
          {t("ONBOARDING_ACCOUNT_VERIFICATION_TITLE")}
        </Typography>
      ) : null}

      <Box
        sx={{
          mt: hideAccountVerificationTitle ? 0 : ONBOARDING_OTP_GAP_TITLE,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {otpSubheaderKey ? (
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              lineHeight: "24px",
              textAlign: "center",
              color: "text.primary",
              width: "100%",
            }}
          >
            {t(otpSubheaderKey)}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              lineHeight: "1.125rem",
              color: "neutral.body",
              textAlign: "center",
            }}
          >
            {t("ONBOARDING_OTP_SENT_PREFIX")}
            <Box component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
              91-{otpMobileDisplay}
            </Box>
          </Typography>
          {onEditNumber ? renderOnboardingEditPencil(t, onEditNumber) : null}
        </Box>
      </Box>

      <Box sx={{ mt: ONBOARDING_OTP_GAP_LABEL, width: "100%", display: "flex", justifyContent: "center" }}>
        <RewampFieldLabel
          label={t("ONBOARDING_OTP_ENTER_LABEL")}
          required
          component="p"
          sx={{ textAlign: "center" }}
        />
      </Box>
    </Box>
  );

  const renderOtpInputBlock = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: isInline ? "auto" : "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: isInline ? "flex-start" : "center",
          flexWrap: "wrap",
        }}
      >
        <OTPinput
          boxGap={1.5}
          otp={otpValue}
          setOtp={setOtpValue}
          setSubmissionStatus={setOtpValidationError}
          inputSx={otpBoxSx}
        />
      </Box>
      {renderOtpFeedback()}
      {renderResendRow()}
    </Box>
  );

  return (
    <Box sx={bare ? undefined : otpCardSx}>
      {onboardingChrome ? renderOnboardingChrome() : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }} className="pb-4">
            <Typography color={"text.primary"}>
              {t("schemes.otpSentMessage", { mobile: mobileNumber })}
            </Typography>
            {onEditNumber && (
              <IconButton
                size="small"
                aria-label={t("schemes.editMobileNumber")}
                onClick={onEditNumber}
                sx={{ color: theme.palette.primary.main }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Typography
            sx={{ fontSize: 14, fontWeight: 600, lineHeight: "20px", color: "text.primary", pb: "4px" }}
          >
            {t("schemes.enterOTP")}
          </Typography>
        </>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleOTPClick();
        }}
        style={{ width: "100%" }}
      >
        <Box sx={{ mt: onboardingChrome ? ONBOARDING_OTP_GAP_INPUTS : 0, width: "100%" }}>
        {layout === "inline" ? (
          <Box
            sx={{
              display: "flex",
              columnGap: "2rem",
              rowGap: "1rem",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "start",
            }}
          >
            {renderOtpInputBlock()}
            {!isSubmit && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Button
                  type="submit"
                  variant="ctaPill"
                  disabled={otpValue.length < 6 || verificationLoading}
                  sx={{ width: "auto", minWidth: "120px" }}
                >
                  {t("schemes.verify")}
                </Button>
                {verificationLoading && (
                  <AccessibleCircularProgress color="success" size="32px" thickness={6} />
                )}
              </Box>
            )}
          </Box>
        ) : (
          <>
            {renderOtpInputBlock()}
            {!isSubmit && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: onboardingChrome ? 0 : 2,
                }}
              >
                <Button
                  type="submit"
                  variant="ctaPill"
                  fullWidth={onboardingChrome}
                  disabled={otpValue.length < 6 || verificationLoading}
                >
                  {t("schemes.verify")}
                </Button>
                {verificationLoading && (
                  <AccessibleCircularProgress color="success" size="32px" thickness={6} />
                )}
              </Box>
            )}
          </>
        )}
        </Box>
      </form>
    </Box>
  );
};

// Exported entry point — always renders OtpInputRendererBody inside the
// rewamp theme (Bihar Krishi green pill/card chrome), regardless of the
// ambient theme the caller lives under. This keeps every consumer visually
// consistent: FarmerLoginForm (RewampLoginPage), FarmerDbtVerification, and InlineFarmerOtp
// already render inside RewampLayout's rewamp ThemeProvider, but the 2 GRM
// call sites (TrackService.js, GrmCreateForm.js) render under the legacy
// theme — this wrapper re-themes just this one body, the same "re-theme one
// body" move RewampRoutes.js's HomeLanding/hortiWrap use for legacy-themed
// pages.
export const OtpInputRenderer = (props) => {
  const { fontSize, ourTheme } = useContext(AppLayoutContext);
  const rewampTheme = useMemo(
    () => generateRewampTheme(fontSize, ourTheme),
    [fontSize, ourTheme],
  );
  return (
    <ThemeProvider theme={rewampTheme}>
      <OtpInputRendererBody {...props} />
    </ThemeProvider>
  );
};

function FarmerDbtVerification({
  handleVerifyOtp,
  otpValidationError,
  setOtpValidationError,
  otpValue,
  setOtpValue,
  categoryType = "PROFILE_GET",
  userType = "CITIZEN",
  verificationLoading = false,
  headerText = "",
  mdmsId,
  onOtpStepChange,
}) {
  // The agent picks how to identify the farmer: by DBT ID (default) or by
  // registered mobile number. The mobile option is only verified to work for
  // identity categoryTypes (PROFILE_GET, SCHEME_APPLY_PROFILE_GET); resource
  // categoryTypes still bundle the dbtId into their SearchCriteria, so keep
  // them DBT-only.
  const allowMobile = isProfileIdentityCategoryType(categoryType);
  const [idMode, setIdMode] = useState("dbtId"); // "dbtId" | "mobile"
  const [dbtValue, setDbtValue] = useState("");
  const [mobileValue, setMobileValue] = useState("");
  // Server-echoed number from _send (masked for DBT lookup); mobile-verify
  // stores the agent-typed full number instead.
  const [mobileNumber, setMobileNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [waitTime, setWaitTime] = useState(null);
  // Inline error banner (Figma 639-82351, "Incorrect DBT ID") — persists next
  // to the field; the single error surface for this form (no toast alongside it).
  const [identifierError, setIdentifierError] = useState("");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:640px)");
  const isDarkTheme = theme.palette.mode === "dark";
  const inputId = useId();
  const { open: urlDialogOpen, externalUrl, handleLinkClick, handleClose: handleUrlDialogClose } =
    useUrlDialog();

  const isMobileMode = allowMobile && idMode === "mobile";
  const identifierValue = isMobileMode ? mobileValue : dbtValue;
  const requiredLength = isMobileMode ? 10 : 13;
  const otpSent = Boolean(mobileNumber);

  // Same DBT/Non-DBT hint used on the login page's mobile field (mobile-parity
  // pre-OTP lookup) — only meaningful in mobile-verify mode, DBT-ID mode
  // already names the identifier directly.
  const {
    loading: dbtHintLoading,
    isRegistered: dbtIsRegistered,
  } = useDbtRegistrationHint(isMobileMode ? identifierValue : "");
  const dbtBadgeLabel =
    !isMobileMode || dbtIsRegistered === null
      ? null
      : dbtIsRegistered
        ? t("ONBOARDING_SIGNIN_DBT")
        : t("ONBOARDING_SIGNIN_NON_DBT");

  const location = useLocation();
  const handleRecover = () => {
    navigate(`${window.contextPath || ""}/recover-dbt-id`, { state: { from: location } });
  };

  const handleModeChange = (event) => {
    // Switching identifier clears both inputs and any sent-OTP state so the
    // "OTP sent" copy / resend timer never point at a stale identifier.
    setIdMode(event.target.value);
    setDbtValue("");
    setMobileValue("");
    setMobileNumber("");
    setWaitTime(null);
    setOtpValue("");
    setOtpValidationError({ message: "" });
    setIdentifierError("");
  };

  const handleDbtChange = (event) => {
    const { value } = event.target;
    if (/^\d*$/.test(value) && value.length <= 13) {
      setDbtValue(value);
      setIdentifierError("");
    }
  };

  const handleMobileChange = (event) => {
    const { value } = event.target;
    if (/^\d*$/.test(value) && value.length <= 10) {
      setMobileValue(value);
      setIdentifierError("");
    }
  };

  const handleClick = async () => {
    setIdentifierError("");
    if (identifierValue.length === 0) {
      const code = isMobileMode ? "EMPTY_MOBILE_NUMBER" : "EMPTY_DBT_ID";
      setIdentifierError(code);
      return;
    } else if (identifierValue.length < requiredLength) {
      const code = isMobileMode ? "INVALID_MOBILE_NUMBER" : "INVALID_DBT_ID";
      setIdentifierError(code);
      return;
    }
    const requestData = {
      otp: {
        mobileNumber: isMobileMode ? mobileValue : null,
        dbtId: isMobileMode ? null : dbtValue,
        categoryType,
        userType,
        tenantId: "br",
        // SCHEME_APPLY_PROFILE_GET scopes the identity OTP to one scheme.
        ...(mdmsId ? { mdmsId } : {}),
      }, 
    };
    setLoading(true);
    try {
      const response = await SchemeService.otpFarmerSchemeGet(requestData);

      if (response?.ResponseInfo?.status === "successful") {
        const contact = response?.message;
        setMobileNumber(isMobileMode ? mobileValue : contact);
        dispatchNotification("success", [t("schemes.otpSuccess")], dispatch);
        setWaitTime(OTP_RESEND_SECONDS);
        setOtpValue("");
      } else {
        const code = response?.Errors?.[0]?.code || "SOMETHING_WENT_WRONG";
        if (code === OTP_RATE_LIMIT_CODE) {
          setIdentifierError("COMMON_MOBILE_RATE_LIMIT");
          return;
        }
        throw new Error(code);
      }
    } catch (error) {
      setIdentifierError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Figma 639-88408's edit-pencil — return to the identifier step without
  // losing what was typed (dbtValue/mobileValue survive; only the
  // "OTP sent" state resets). Contrast with handleModeChange, which is a
  // full reset including the identifier values.
  const handleEditNumber = () => {
    setMobileNumber("");
    setWaitTime(null);
    setOtpValue("");
    setOtpValidationError({ message: "" });
    setIdentifierError("");
  };

  // Breadcrumb's Back pill lives outside this component (FarmerContextRoute).
  // Report the "go back to identifier step" handler only while the OTP
  // screen is showing, so Back can return here instead of leaving the page;
  // on the identifier step itself, null restores the breadcrumb's default
  // (up-one-level) behaviour.
  useEffect(() => {
    onOtpStepChange?.(otpSent ? handleEditNumber : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpSent]);

  return (
    // Card + Recover/Register are siblings (gap 2), same max-width so left edges align.
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          ...onboardingCardSx,
          maxWidth: LOGIN_CARD_MAX_WIDTH,
          mx: "auto",
          alignItems: "center",
        }}
      >
        {!otpSent ? (
          <Typography
            variant={isMobile ? "h5" : "h3"}
            component="h2"
            sx={{ textAlign: "center", width: "100%", color: "text.primary" }}
          >
            {headerText ? t(headerText) : t("schemes.schemeVerifyFarmer")}
          </Typography>
        ) : null}

        {!otpSent ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
            {allowMobile && (
              <FormControl component="fieldset" sx={{ width: "100%" }}>
                <Typography component="legend" sx={visuallyHiddenSx}>
                  {t("schemes.verifyUsing")}
                </Typography>
                <RadioGroup
                  row
                  name="farmer-identifier-mode"
                  value={idMode}
                  onChange={handleModeChange}
                  sx={{
                    width: "100%",
                    justifyContent: "space-between",
                    borderBottom: 2,
                    borderColor: "divider",
                  }}
                >
                  <FormControlLabel
                    value="dbtId"
                    control={<Radio color="primary" />}
                    label={t("schemes.verifyByDbtId")}
                    sx={{ flex: 1, m: 0, pb: 1.5, justifyContent: "flex-start" }}
                  />
                  <FormControlLabel
                    value="mobile"
                    control={<Radio color="primary" />}
                    label={t("schemes.verifyByMobile")}
                    sx={{ flex: 1, m: 0, pb: 1.5, justifyContent: "flex-start" }}
                  />
                </RadioGroup>
              </FormControl>
            )}
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleClick();
              }}
              noValidate
            >
              <OnboardingFieldLabel required htmlFor={inputId} sx={{ pb: 0.5 }}>
                {isMobileMode
                  ? t("schemes.farmerMobileNumber")
                  : t("schemes.farmerDBTId")}
              </OnboardingFieldLabel>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <CssTextField
                  value={identifierValue}
                  onChange={isMobileMode ? handleMobileChange : handleDbtChange}
                  placeholder={
                    isMobileMode
                      ? t("schemes.farmerMobileNumberPlaceholder")
                      : t("schemes.farmerDBTIdPlaceholder")
                  }
                  inputProps={{ inputMode: "numeric", maxLength: requiredLength }}
                  InputProps={
                    isMobileMode
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              <DbtRegistrationBadge loading={dbtHintLoading} label={dbtBadgeLabel} />
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                  sx={{
                    width: "100%",
                    ...onboardingInputSx,
                    // CssTextField doubles the outlined-root class; match that specificity.
                    "& .MuiOutlinedInput-root.MuiOutlinedInput-root input": {
                      padding: `${theme.spacing(1.5)} !important`,
                    },
                    "& .MuiOutlinedInput-root fieldset": {
                      borderColor: "divider",
                      borderRadius: "8px",
                    },
                  }}
                  darkTheme={isDarkTheme}
                  id={inputId}
                />
                {identifierError && (
                  <RewampFeedbackBanner variant="error" message={t(identifierError)} />
                )}
                <Button
                  type="submit"
                  variant="ctaPill"
                  disabled={loading || identifierValue.length < requiredLength}
                >
                  {t("schemes.getOTP")}
                </Button>
              </Box>
            </Box>
            {loading && (
              <Typography color="text.textGreen" sx={{ mt: 1 }}>
                {t("schemes.sendingOTP")}...
              </Typography>
            )}
          </Box>
        ) : (
          <OtpInputRenderer
            bare
            onboardingChrome
            otpSubheaderKey={isMobileMode ? "ONBOARDING_RECOVER_OTP_TITLE" : "ONBOARDING_DBT_ID_OTP_TITLE"}
            mobileNumber={mobileNumber}
            mobileNumberDisplay={isMobileMode ? mobileValue : mobileNumber}
            otpValue={otpValue}
            setOtpValue={setOtpValue}
            otpValidationError={otpValidationError}
            setOtpValidationError={setOtpValidationError}
            handleVerifyOtp={() =>
              handleVerifyOtp(
                otpValue,
                isMobileMode
                  ? { mobileNumber: mobileValue }
                  : { dbtId: dbtValue },
              )
            }
            dispatch={dispatch}
            t={t}
            waitTime={waitTime}
            setWaitTime={setWaitTime}
            onResendOtp={handleClick}
            onEditNumber={handleEditNumber}
            verificationLoading={verificationLoading}
          />
        )}
      </Box>
      {!otpSent && !isMobileMode && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
            maxWidth: LOGIN_CARD_MAX_WIDTH,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ color: "neutral.body" }}>
              {t("schemes.cantRememberDbtId")}
            </Typography>
            <Button variant="tertiaryCta" onClick={handleRecover}>
              {t("schemes.recover")}
            </Button>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ color: "neutral.body" }}>
              {t("schemes.dontHaveDbtId")}
            </Typography>
            <Button
              variant="tertiaryCta"
              onClick={() => handleLinkClick(DBT_REGISTRATION_PORTAL_URL)}
            >
              {t("schemes.registerNow")}
            </Button>
          </Box>
        </Box>
      )}
      <UrlDialog
        open={urlDialogOpen}
        externalUrl={externalUrl}
        handleClose={handleUrlDialogClose}
        t={t}
      />
    </Box>
  );
}

export default FarmerDbtVerification;
